import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import {
  Toll as CreditIcon,
  Check as CheckIcon,
  LocalOffer as BestValueIcon,
} from '@mui/icons-material';
import { productsApi, type PaymentProduct, type PaymentPrice } from '../../api';
import { useCredits } from '../../hooks';
import { CheckoutModal } from './CheckoutModal';

export interface CreditsPurchaseModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Called when the modal is closed (cancelled) */
  onClose: () => void;
  /** Called when credits are successfully purchased */
  onSuccess?: () => void;
  /** The number of credits required for the action */
  requiredCredits: number;
  /** Context message explaining what the credits are needed for */
  contextMessage?: string;
}

interface CreditPackOption {
  product: PaymentProduct;
  price: PaymentPrice;
  credits: number;
  pricePerCredit: number;
  isBestValue: boolean;
}

/**
 * Modal for purchasing credits in-context when the user needs more credits
 * to complete an action (like creating a premium board).
 *
 * Shows:
 * - Current balance vs required credits
 * - Available credit pack options with pricing
 * - Highlights best value option
 * - Transitions to CheckoutModal for payment
 */
export const CreditsPurchaseModal: React.FC<CreditsPurchaseModalProps> = ({
  open,
  onClose,
  onSuccess,
  requiredCredits,
  contextMessage,
}) => {
  const { credits, refreshCredits } = useCredits();
  const [creditPacks, setCreditPacks] = useState<PaymentProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPack, setSelectedPack] = useState<CreditPackOption | null>(null);
  const [showCheckout, setShowCheckout] = useState(false);

  // Calculate how many credits are needed
  const availableCredits = credits?.availableCredits ?? 0;
  const creditsNeeded = Math.max(0, requiredCredits - availableCredits);

  // Fetch credit packs when modal opens
  useEffect(() => {
    if (open) {
      setLoading(true);
      setError(null);
      setSelectedPack(null);
      setShowCheckout(false);

      productsApi.listCreditPacks()
        .then(packs => {
          setCreditPacks(packs);
          setLoading(false);
        })
        .catch(err => {
          setError(err instanceof Error ? err.message : 'Failed to load credit packs');
          setLoading(false);
        });
    }
  }, [open]);

  // Process credit packs into options with calculated values
  const packOptions: CreditPackOption[] = useMemo(() => {
    const options: CreditPackOption[] = [];

    for (const product of creditPacks) {
      const packCredits = product.metadata?.boardCredits || 0;
      if (packCredits <= 0) continue;

      // Get the active price (prefer first one)
      const price = product.prices.find(p => p.active);
      if (!price) continue;

      options.push({
        product,
        price,
        credits: packCredits,
        pricePerCredit: price.amount / packCredits,
        isBestValue: false,
      });
    }

    // Sort by credits ascending
    options.sort((a, b) => a.credits - b.credits);

    // Find the best value (lowest price per credit) among packs that cover the need
    const coveringPacks = options.filter(o => o.credits >= creditsNeeded);
    let bestValueProductId: string | null = null;

    if (coveringPacks.length > 0) {
      const bestValue = coveringPacks.reduce((best, current) =>
        current.pricePerCredit < best.pricePerCredit ? current : best
      );
      bestValueProductId = bestValue.product.id;
    } else if (options.length > 0) {
      // If no single pack covers the need, mark the highest value pack
      const bestValue = options.reduce((best, current) =>
        current.pricePerCredit < best.pricePerCredit ? current : best
      );
      bestValueProductId = bestValue.product.id;
    }

    // Return new array with isBestValue flag set immutably
    return options.map(opt => ({
      ...opt,
      isBestValue: opt.product.id === bestValueProductId,
    }));
  }, [creditPacks, creditsNeeded]);

  // Auto-select the best value pack when pack options change (e.g., after loading)
  // Only runs when packOptions changes, not when selectedPack changes
  useEffect(() => {
    if (packOptions.length > 0) {
      const bestValuePack = packOptions.find(p => p.isBestValue);
      if (bestValuePack) {
        setSelectedPack(bestValuePack);
      }
    }
  }, [packOptions]);

  const handlePackSelect = (pack: CreditPackOption) => {
    setSelectedPack(pack);
  };

  const handleProceedToCheckout = () => {
    if (selectedPack) {
      setShowCheckout(true);
    }
  };

  const handleCheckoutSuccess = async () => {
    await refreshCredits();
    setShowCheckout(false);
    onSuccess?.();
  };

  const handleCheckoutClose = () => {
    setShowCheckout(false);
  };

  const handleClose = () => {
    if (!showCheckout) {
      onClose();
    }
  };

  // If showing checkout, render the CheckoutModal instead
  if (showCheckout && selectedPack) {
    return (
      <CheckoutModal
        open={true}
        onClose={handleCheckoutClose}
        onSuccess={handleCheckoutSuccess}
        product={selectedPack.product}
        price={selectedPack.price}
      />
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CreditIcon color="primary" />
          Purchase Credits
        </Box>
      </DialogTitle>
      <DialogContent>
        {/* Context and balance info */}
        <Box sx={{ mb: 3 }}>
          {contextMessage && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {contextMessage}
            </Typography>
          )}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              bgcolor: 'background.default',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box>
              <Typography variant="body2" color="text.secondary">
                Current Balance
              </Typography>
              <Typography variant="h6">
                {availableCredits} Credit{availableCredits !== 1 ? 's' : ''}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" color="text.secondary">
                Required
              </Typography>
              <Typography variant="h6" color="primary">
                {requiredCredits} Credit{requiredCredits !== 1 ? 's' : ''}
              </Typography>
            </Box>
          </Box>

          {creditsNeeded > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              You need {creditsNeeded} more credit{creditsNeeded !== 1 ? 's' : ''} to continue.
            </Alert>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Credit pack options */}
        <Typography variant="subtitle1" sx={{ mb: 2 }}>
          Choose a Credit Pack
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : packOptions.length === 0 ? (
          <Alert severity="warning">No credit packs available</Alert>
        ) : (
          <Stack spacing={2}>
            {packOptions.map((pack) => {
              const isSelected = selectedPack?.product.id === pack.product.id;
              const coversNeed = pack.credits >= creditsNeeded;

              return (
                <Card
                  key={pack.product.id}
                  variant="outlined"
                  sx={{
                    position: 'relative',
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    borderWidth: isSelected ? 2 : 1,
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: isSelected ? 'primary.main' : 'primary.light',
                    },
                  }}
                >
                  <CardActionArea onClick={() => handlePackSelect(pack)}>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="h6" component="span">
                              {pack.product.name}
                            </Typography>
                            {pack.isBestValue && (
                              <Chip
                                icon={<BestValueIcon />}
                                label="Best Value"
                                size="small"
                                color="success"
                                variant="filled"
                              />
                            )}
                            {coversNeed && !pack.isBestValue && creditsNeeded > 0 && (
                              <Chip
                                icon={<CheckIcon />}
                                label="Covers Need"
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            )}
                          </Box>
                          <Typography variant="body2" color="text.secondary">
                            {pack.credits} Credit{pack.credits !== 1 ? 's' : ''}
                            {' '}&bull;{' '}
                            ${(pack.pricePerCredit / 100).toFixed(2)} per credit
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="h5" color="primary">
                            ${(pack.price.amount / 100).toFixed(2)}
                          </Typography>
                        </Box>
                      </Box>
                      {isSelected && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <CheckIcon sx={{ fontSize: 16, color: 'white' }} />
                        </Box>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              );
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleProceedToCheckout}
          disabled={!selectedPack || loading}
          startIcon={<CreditIcon />}
        >
          {selectedPack
            ? `Buy ${selectedPack.credits} Credits - $${(selectedPack.price.amount / 100).toFixed(2)}`
            : 'Select a Pack'
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreditsPurchaseModal;
